import React, { useRef, useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, registerables } from 'chart.js';
import ErrorBoundary from '../../../../components/Common/ErrorBoundary';

ChartJS.register(...registerables);

const severityGradientMap = {
    //'Extreme': ['#dd2121', '#fa4646'],
    //'High': ['#d06841', '#fa6028'],
    //'Moderate': ['#daa926', '#f7b607'],
    //'Low': ['#83ba2b', '#96eb0e'],
    //'Offline': ['#F32C22', '#FF6B6B'], // Gradient from red to light red
    //'Online': ['#7BB526', '#A8E063'], // Gradient from green to light green
    'Non-Integrated': ['#747474', '#A9A9A9'], // Gradient from dark gray to light gray
    //'Faulty': ['#f44336', '#FF7961'], // Gradient from red to light red
    //'Normal': ['#28a745', '#81C784'], // Gradient from green to light green
    //'Unknown': ['#131415', '#4B4B4B'], // Gradient from black to dark gray
    'Integrated': ['#0B70D1', '#63A4FF'] // Gradient from blue to light blue
};

const BarChart = ({isDarkTheme, BarWidgetDetails, widgetConfig, handleModalShow, widgetData}) => {
  const chartRef = useRef(null);
  const { 
    WidgetName = 'Bar Chart', 
    ColorSets = ['#8400ff', '#f5208a'],
    Datapoints = ['y-axis','x-axis'],
    WidgetID = '', Entity = '' } = widgetConfig || {}

  

  const { WidgetData = {} } = widgetData || {};

  // Assign yName and xName with fallbacks
  const yName = Datapoints && Datapoints.length > 0 ? Datapoints[0] : 'y-axis';
  const xName = Datapoints && Datapoints.length > 1 ? Datapoints[1] : 'x-axis';  

  const labels = WidgetData[xName] || [];
  const dataValues = WidgetData[yName] || [];

  const data = {
    labels,
    datasets: [
        {
            label: 'Dataset',
            data: dataValues,
            backgroundColor: (context) => {
                const chart = context.chart;
                const { ctx, chartArea } = chart;

                if (!chartArea) return null; // Skip until layout is ready

                const index = context.dataIndex;
                const label = labels[index];

                // Check if the label exists in severityGradientMap
                if (severityGradientMap[label]) {
                    const [start, end] = severityGradientMap[label];

                    // Create gradient for the bar
                    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                    gradient.addColorStop(0, start);
                    gradient.addColorStop(1, end);
                    return gradient;
                }

                // Default gradient if no match is found
                const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                gradient.addColorStop(0, ColorSets[0] || '#8400ff');
                gradient.addColorStop(1, ColorSets[1] || '#f5208a');
                return gradient;
            },
            borderRadius: 5,
            barThickness: 20,
        }
    ]
};

  const options = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index' },
      plugins: {
          legend: { display: false },
          tooltip: {
              callbacks: {
                  label: function (tooltipItem) {
                      let dataset = tooltipItem.dataset;
                      let value = dataset.data[tooltipItem.dataIndex];
                      let label = dataset.labels ? dataset.labels[tooltipItem.dataIndex] : "";
                      return `${label}: ${value}`;
                  },
              },
          },
      },
      onClick: (event, elements) => {
          if (elements && elements.length > 0) {
              const chart = chartRef.current;
              const element = elements[0];
              const datasetIndex = element.datasetIndex;
              const index = element.index;
              // You can pass more info if needed
              const filter = {
                [xName]: data.labels[index]
              }

            const today = new Date();
            const yyyy = today.getFullYear();
            const mm = String(today.getMonth() + 1).padStart(2, '0');
            const dd = String(today.getDate()).padStart(2, '0');
            //const csvFileName = `${Entity.toLowerCase()}_data_${yyyy}-${mm}-${dd}`;
            const csvFileName = `${Entity.toLowerCase()}_${data.labels[index].toLowerCase()}_data_${yyyy}-${mm}-${dd}`;

            handleModalShow && handleModalShow(filter, Entity, WidgetID, csvFileName);
          }
      },
      onHover: (event, elements) => {
        if (event?.native) {
            event.native.target.style.cursor = elements && elements.length > 0 ? 'pointer' : 'default';
        }
    },
      scales: {
          x: {
              grid: { display: false },
              border: {
                  display: true,
                  color: isDarkTheme ? '#e6e6e6' : '#bababa',
              },
              ticks: {
                  color: isDarkTheme ? '#888888' : '#bababa',
              },
              title: {
                  display: true,
                  text: xName,
                  color: isDarkTheme ? '#555' : '#bababa',
              },
          },
          y: {
              beginAtZero: true,
              grid: { display: false },
              border: {
                  display: true,
                  color: isDarkTheme ? '#e6e6e6' : '#bababa',
              },
              ticks: {
                  color: isDarkTheme ? '#bababa' : '#bababa',
                  stepSize: 1,      // <-- Add this line
                  precision: 0,     // <-- Add this line
              },
              title: {
                  display: true,
                  text: yName,
                  color: isDarkTheme ? '#555' : '#bababa',
              },
          },
      },
  };

  // Show "No Data Available" if widgetData or widgetData.WidgetData is missing or empty
  if (!widgetData || !widgetData.WidgetData || dataValues?.length === 0 || labels?.length === 0) {
    return (
      <ErrorBoundary>
        <div className='systemHealth m-0 WidgetPadding p-0' key={`widget-${WidgetID}`}>
          <p className='chartHeaderP p-2'>{WidgetName}  
            { widgetConfig.DateRange
              ? widgetConfig.DateRange === 1
                ? ` `
                : ` - ${widgetConfig.DateRange} Days`
              : ''}
            </p>
          <div style={{ padding: '10px', height: '85%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: '#888', fontWeight: 500 }}>No Data Available</span>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div className='systemHealth m-0 WidgetPadding p-0' key={`widget-${WidgetID}`}>
          <p className='chartHeaderP p-2'>{WidgetName}
            { widgetConfig.DateRange
              ? widgetConfig.DateRange === 1
                ? ` `
                : ` - ${widgetConfig.DateRange} Days`
              : ''}
          </p>
          <div style={{ padding: '10px', height: '85%' }}>
              <Bar ref={chartRef} data={data} options={options} />
          </div>
      </div>
    </ErrorBoundary>
  );
}

export default BarChart